// 시장 이슈 · 트렌드 탭의 본문 — 키워드가 뉴스에서 얼마나 다뤄졌는지.
//
// 레이아웃은 트레이딩 터미널 방식이다(Bishop 지정): 왼쪽 키워드 패널(MarketPage)은
// 그대로 두고, 가운데는 차트가 화면 높이를 꽉 채우고, 오른쪽에 기사 목록이 붙는다.
// 다른 탭처럼 흰 카드에 담지 않는다 — 트렌드는 읽을 문서가 아니라 보는 도구다.
//
// 조회는 **누를 때만** 나간다. 값이 바뀔 때마다 자동으로 받으면 날짜를 고치는 도중이나
// 차트 손잡이를 살짝 건드릴 때마다 검색이 나간다(Bishop):
//   · 기간 → 날짜는 초안에 담고 '조회' 를 눌러야 차트·기사를 함께 받는다
//   · 차트 드래그 → 고른 구간을 보여주기만 하고, '이 기간 기사 검색' 을 눌러야 기사를
//     받는다. 이때 차트는 다시 그리지 않는다 — 다시 그리면 방금 고른 구간이 사라진다.
// 안내 문구에 '오른쪽' 같은 방향을 쓰지 않는다 — 좁은 화면에서는 기사가 아래로 내려간다.
import { useEffect, useRef, useState } from "react";
import { chartHtml } from "../lib/markdown";
import {
  DATA_START, TREND_INTERVALS, autoInterval, indexOfDate, pageItems, statsFor,
  useTrend, useTrendFacets, useTrendNews,
} from "../lib/marketTrend";
import { VAChart } from "../lib/vachart";
import { DateRange, daysAgo, isoDate } from "../ui/DateRange";

const stroke = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.7,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

/** 기본 기간 — 10년(Bishop). 데이터는 1990년부터 있지만 그 시절은 수집된 전체
 *  뉴스량 자체가 적어 비중이 크게 튄다. 서버 news_trend.DEFAULT_DAYS 와 같은 값이다. */
const DEFAULT_DAYS = 3650;
/** 차트 첫 그림의 높이 — 그 뒤로는 CSS 가 남은 높이를 채우고 echarts 가 따라온다 */
const CHART_H = 420;
const SIDE_KEY = "va_market_trend_side_w";
const SIDE_MIN = 300;
const SIDE_MAX = 560;
const SIDE_DEFAULT = 340;

function savedWidth(): number {
  try {
    const n = Number(localStorage.getItem(SIDE_KEY));
    return n >= SIDE_MIN && n <= SIDE_MAX ? n : SIDE_DEFAULT;
  } catch {
    return SIDE_DEFAULT;
  }
}

/** 건수 — 자릿수가 커도 읽히게 천 단위 구분 */
function cnt(n: number): string {
  return Math.round(n).toLocaleString("ko-KR");
}

/** 하루 평균은 한 자리 소수까지 — 0건과 0.4건은 다른 뜻이다 */
function perDay(n: number): string {
  return n >= 10 ? cnt(n) : n.toFixed(1);
}

/** 2026-06-01 → 2026년 6월 (월별·주별에서 '01' 이 뜻 없이 붙어 보인다) */
function whenLabel(key: string | null | undefined, interval: string): string {
  if (!key) return "—";
  const [y, m, d] = String(key).split("-");
  if (interval === "1M") return `${y}년 ${Number(m)}월`;
  return `${y}년 ${Number(m)}월 ${Number(d)}일`;
}

interface Span { from: string; to: string }

/** 한 차트에서 견줄 수 있는 키워드 수 — 서버 news_trend.MAX_QUERIES 와 같아야 한다 */
export const TREND_MAX = 5;

export interface MarketTrendProps {
  /** 보고 있는 키워드 — 첫 번째가 기본(왼쪽 패널·검색창이 정한다), 뒤는 비교용 */
  keywords: string[];
  /** 비교 입력의 제안 목록 — 서버가 준 투자 테마 */
  themes: string[];
  onAddKeyword: (kw: string) => void;
  onRemoveKeyword: (kw: string) => void;
}

export default function MarketTrend({
  keywords, themes, onAddKeyword, onRemoveKeyword,
}: MarketTrendProps) {
  const [range, setRange] = useState(() => ({
    from: daysAgo(DEFAULT_DAYS), to: isoDate(new Date()),
  }));
  // 고르는 중인 기간 — 날짜 입력과 차트 드래그가 **같은 값**을 쓴다. 눌러야 조회한다:
  // 값이 바뀔 때마다 받으면 날짜를 고치는 도중이나 손잡이를 살짝 건드릴 때마다 검색이
  // 나간다(Bishop). 차트에서 고른 구간이 위 달력에 그대로 보이는 것도 이 때문이다.
  const [draft, setDraft] = useState(range);
  // 차트에 **보이는** 구간(확대 상태). 조회 구간과 다를 수 있다 — 지표는 이걸 따른다.
  const [view, setView] = useState<Span | null>(null);
  // 빈 문자열이면 기간 길이에 맞춰 자동으로 고른다
  const [interval, setInterval] = useState("");
  const [page, setPage] = useState(1);
  // 차트를 처음 상태로 되돌릴 때 올린다 — 확대는 echarts 안에만 있어서 다시 그려야 풀린다
  const [redraw, setRedraw] = useState(0);
  // 기사 목록은 키워드 하나만 본다 — 여러 개를 OR 로 묶으면 어느 키워드 기사인지
  // 알 수 없다. 기본은 첫 번째, 칩을 눌러 바꾼다.
  const [newsKw, setNewsKw] = useState("");
  // 비교 입력 — 접혀 있다가 '+ 비교 키워드 추가' 를 누르면 열린다(채팅 주가 차트와 같은 방식)
  const [cmpOpen, setCmpOpen] = useState(false);
  const [cmpQ, setCmpQ] = useState("");
  const [sideW, setSideW] = useState<number>(savedWidth);
  const [dragging, setDragging] = useState(false);
  const chartRef = useRef<HTMLDivElement | null>(null);
  // 차트 이벤트 핸들러가 최신 기간을 봐야 한다 — 핸들러는 차트를 그릴 때 한 번만 묶인다
  const rangeRef = useRef(range);
  rangeRef.current = range;

  const activeIv = interval || autoInterval(range.from, range.to);
  const trend = useTrend(keywords, range.from, range.to, activeIv);
  // 고른 키워드가 목록에서 빠지면 첫 번째로 되돌린다
  const shownKw = keywords.includes(newsKw) ? newsKw : (keywords[0] || "");
  const news = useTrendNews(shownKw, range.from, range.to, page);
  // 함께 언급된 기업·분야 — 표본이라 조회 기간을 따른다(차트 확대는 따라가지 않는다)
  const facets = useTrendFacets(keywords[0] || "", range.from, range.to);

  // 키워드·구간이 바뀌면 첫 페이지부터 — 3페이지를 보던 중 구간을 좁히면 빈 화면이 된다
  useEffect(() => {
    setPage(1);
  }, [shownKw, range.from, range.to]);

  // 조회한 기간이 바뀌면 달력도 그 값으로 — 눌러서 반영된 뒤에는 초안이 따로 남지 않는다
  useEffect(() => {
    setDraft(range);
    setView(null);
  }, [range]);

  // 차트는 채팅과 같은 방식으로 그린다 — .chart-box[data-chart] 를 심고 hydrate.
  // 드래그로 구간을 좁히면 vachart 가 va:zoom 을 띄운다(전체로 돌리면 full:true).
  useEffect(() => {
    const box = chartRef.current;
    if (!box) return;
    VAChart.dispose(box);
    // 높이는 첫 그림에만 쓰인다 — 이후엔 CSS 가 남은 높이를 채우고, vachart 의
    // ResizeObserver 가 캔버스 크기 변화를 받아 차트를 다시 맞춘다.
    // smooth:false — 꺾은선으로 잇는다(Bishop). 서버 spec 에도 넣었지만 배포가
    // 늦거나 캐시된 응답이 오면 옛 spec(곡선)이 그대로 오므로 화면에서 한 번 더 정한다.
    const spec = trend.data?.spec
      ? { ...trend.data.spec, smooth: false, height: CHART_H }
      : null;
    box.innerHTML = spec ? chartHtml(spec) : "";
    if (spec) VAChart.hydrate(box);
    // 드래그로 고른 구간을 위 달력에 그대로 넣는다(Bishop) — 조회는 버튼을 눌러야 한다.
    // 손잡이를 끝까지 되돌리면(full) 조회한 기간으로 되돌아간다.
    const onZoom = (e: Event) => {
      const d = (e as CustomEvent).detail as { from: string; to: string; full: boolean } | null;
      if (!d) return;
      setDraft(d.full ? rangeRef.current : { from: d.from, to: d.to });
      setView(d.full ? null : { from: d.from, to: d.to });
    };
    box.addEventListener("va:zoom", onZoom);
    return () => {
      box.removeEventListener("va:zoom", onZoom);
      VAChart.dispose(box);
    };
  }, [trend.data, redraw]);

  /** 기사 패널 폭 끌기 — 왼쪽으로 끌면 넓어지므로 부호가 반대다(MarketPage 와 같은 방식) */
  const startResize = (e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const start = sideW;
    setDragging(true);
    document.body.classList.add("mk-resizing");
    const move = (ev: MouseEvent) => {
      setSideW(Math.min(SIDE_MAX, Math.max(SIDE_MIN, start - (ev.clientX - startX))));
    };
    const up = () => {
      document.removeEventListener("mousemove", move);
      document.removeEventListener("mouseup", up);
      document.body.classList.remove("mk-resizing");
      setDragging(false);
      setSideW((w) => {
        try {
          localStorage.setItem(SIDE_KEY, String(w));
        } catch {
          /* noop */
        }
        return w;
      });
    };
    document.addEventListener("mousemove", move);
    document.addEventListener("mouseup", up);
  };

  // 보이는 구간의 인덱스 — 확대하지 않았으면 전체다
  const xs = trend.data?.x || [];
  const lo = view ? indexOfDate(xs, view.from, 0) : 0;
  const hi = view ? indexOfDate(xs, view.to, xs.length - 1) : xs.length - 1;
  const rawSeries = trend.data?.series || [];
  const stats = xs.length && rawSeries[0]
    ? statsFor(xs, rawSeries[0].counts, lo, hi)
    : null;
  const cmpStats = xs.length
    ? rawSeries.map((sr) => ({ query: sr.query, ...statsFor(xs, sr.counts, lo, hi) }))
    : [];

  // 비교 제안 — 담긴 것은 빼고, 입력이 있으면 걸러 보여준다
  const cmpHits = themes
    .filter((t) => !keywords.includes(t) && (!cmpQ.trim() || t.includes(cmpQ.trim())))
    .slice(0, 8);

  const zoomable = !!trend.data?.spec?.zoom;
  // 기간이 바뀌었고 뒤집히지 않았을 때만 누를 수 있다 — 버튼은 늘 자리에 있고 꺼져 있다
  const dirty = (draft.from !== range.from || draft.to !== range.to) && draft.from <= draft.to;
  /** 고른 기간으로 조회 — 주기는 새 구간 길이에 맞춰 다시 자동으로 */
  const apply = () => {
    if (!dirty) return;
    setRange(draft);
    setInterval("");
  };

  const base = { from: daysAgo(DEFAULT_DAYS), to: isoDate(new Date()) };
  const atBase = range.from === base.from && range.to === base.to && !view;

  /** 기간 초기화 — 기본 구간(10년)으로 되돌리고 차트 확대도 푼다. 되돌릴 것이
   *  없으면(이미 기본이고 확대도 안 했으면) 꺼져 있다. */
  const reset = () => {
    setInterval("");
    setView(null);
    setDraft(base);
    setRange(base);
    setRedraw((n) => n + 1);
  };

  /** 검색 버튼 — 위(도구 줄)와 아래(차트 밑) 두 곳에 같은 것을 둔다. 차트를 끌고 나면
   *  시선이 아래에 있어서 위 버튼이 멀다(Bishop). */
  const searchBtn = (
    <button
      type="button"
      className={`mk-tr-go${dirty ? " on" : ""}`}
      onClick={apply}
      disabled={!dirty || trend.loading}
    >
      <svg viewBox="0 0 24 24" {...stroke}>
        <circle cx="11" cy="11" r="7" />
        <path d="m20 20-3.5-3.5" />
      </svg>
      이 기간 기사 검색
    </button>
  );

  const resetBtn = (
    <button
      type="button"
      className="mk-tr-go"
      onClick={reset}
      disabled={atBase || trend.loading}
      title="기간을 최근 10년으로 되돌립니다"
    >
      <svg viewBox="0 0 24 24" {...stroke}>
        <path d="M3 12a9 9 0 1 0 3-6.7" />
        <path d="M3 4v5h5" />
      </svg>
      초기화
    </button>
  );

  return (
    /* 넓으면 차트 | 기사 두 열, 좁으면 기사가 차트 아래로 내려간다(CSS). 두 칸을
       한 번 감싸야 세로로 쌓을 수 있다 — 왼쪽 키워드 패널은 그대로 옆에 남는다. */
    <div className="mk-tr-wrap">
      <div className="mk-tr-cols">
      {/* ── 가운데: 차트 ──────────────────────────────────── */}
      <section className="mk-tr-main">
        <div className="mk-tr-bar">
          <div className="mk-tr-id">
            {/* 제목은 기본 키워드 하나 — 비교 중인 것은 차트 위 비교 바가 보여준다 */}
            <span className="mk-tr-kw">{keywords[0] || ""}</span>
          </div>
          <div className="mk-tr-tools">
            <div className="mk-tr-ivs">
              {TREND_INTERVALS.map((iv) => (
                <button
                  key={iv.key}
                  type="button"
                  className={`mk-tr-iv${activeIv === iv.key ? " on" : ""}`}
                  onClick={() => setInterval(iv.key)}
                >
                  {iv.label}
                </button>
              ))}
            </div>
            <DateRange
              from={draft.from}
              to={draft.to}
              onChange={setDraft}
              min={DATA_START}
              max={isoDate(new Date())}
              disabled={trend.loading}
            />
            {searchBtn}
            {resetBtn}
          </div>
        </div>

        {/* 비교 바 — 채팅 주가 차트(.cmp-bar)와 같은 방식이다: 칩으로 무엇을 견주는지
            보여주고, 점선 버튼으로 더한다. 비교는 **옵션**이라 기본은 칩 하나다(Bishop). */}
        <div className="mk-cmp-bar">
          {keywords.map((k, i) => (
            <span key={k} className="mk-cmp-chip">
              {/* 머리글자 원형 배지 — 주가 차트의 .cmp-badge 와 같은 방식(색이 계열과 짝) */}
              <i className={`mk-cmp-badge c${i % 6}`} aria-hidden="true">{k.trim().charAt(0) || "?"}</i>
              <span className="mk-cmp-name">{k}</span>
              {keywords.length > 1 && (
                <button type="button" aria-label={`${k} 빼기`} onClick={() => onRemoveKeyword(k)}>
                  <svg viewBox="0 0 24 24" {...stroke}><path d="M6 6l12 12M18 6L6 18" /></svg>
                </button>
              )}
            </span>
          ))}

          {keywords.length >= TREND_MAX ? (
            <span className="mk-cmp-full">최대 {TREND_MAX}개까지 견줄 수 있습니다</span>
          ) : cmpOpen ? (
            <span className="mk-cmp-search">
              <input
                className="mk-cmp-input"
                autoFocus
                value={cmpQ}
                placeholder="키워드 입력"
                aria-label="비교할 키워드"
                onChange={(e) => setCmpQ(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && cmpQ.trim()) {
                    onAddKeyword(cmpQ.trim());
                    setCmpQ("");
                    setCmpOpen(false);
                  }
                  if (e.key === "Escape") { setCmpQ(""); setCmpOpen(false); }
                }}
                onBlur={() => { if (!cmpQ.trim()) setCmpOpen(false); }}
              />
              {!!cmpHits.length && (
                <span className="mk-cmp-hits">
                  {cmpHits.map((t) => (
                    <button
                      key={t}
                      type="button"
                      className="mk-cmp-hit"
                      // onBlur 보다 먼저 실행돼야 한다 — mousedown 으로 잡는다
                      onMouseDown={(e) => {
                        e.preventDefault();
                        onAddKeyword(t);
                        setCmpQ("");
                        setCmpOpen(false);
                      }}
                    >
                      {t}
                    </button>
                  ))}
                </span>
              )}
            </span>
          ) : (
            <button type="button" className="mk-cmp-add" onClick={() => setCmpOpen(true)}>
              + 비교 키워드 추가
            </button>
          )}
        </div>

        {/* 지표는 **차트에 보이는 구간**을 따른다 — 드래그로 좁히면 그 구간 기준으로
            다시 계산한다(조회를 다시 하지 않고). 값은 비중(%)이 아니라 건수·방향으로
            말한다: "1만 건 중 226건" 은 읽는 사람이 한 번 더 환산해야 한다(Bishop). */}
        {keywords.length === 1 && stats && !stats.empty && (
          <dl className="mk-tr-stats">
            <div>
              <dt>하루 평균</dt>
              <dd>{perDay(stats.perDay)}건</dd>
              <span>{stats.from} ~ {stats.to}</span>
            </div>
            <div>
              <dt>가장 많았던 때</dt>
              <dd>{whenLabel(stats.peakAt, activeIv)}</dd>
              <span>{cnt(stats.peakCount)}건</span>
            </div>

            {/* 아래 둘은 **표본**이다 — 절대 건수가 아니라 순위·비율로 읽어야 해서
                "최근 N건 기준" 을 함께 적는다. 차트 확대는 따라가지 않는다(조회 기간). */}
            <div className="mk-tr-facet">
              <dt>함께 언급된 기업</dt>
              <dd>
                {facets.companies.length ? (
                  <div className="mk-tr-cos">
                    {facets.companies.slice(0, 5).map((c) => (
                      <span key={c.symbol || c.name} className="mk-tr-co">
                        {c.name}
                        <i>{c.count}</i>
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className="mk-tr-none">{facets.loading ? "세는 중…" : "—"}</span>
                )}
              </dd>
              <span>최근 {cnt(facets.sampled)}건 기준</span>
            </div>

            <div className="mk-tr-facet">
              <dt>어떤 분야 뉴스인가</dt>
              <dd>
                {facets.sections.length ? (
                  <div className="mk-tr-secs">
                    {facets.sections.slice(0, 4).map((sc) => (
                      <span key={sc.key} className="mk-tr-sec">
                        <b>{sc.label}</b>
                        <i style={{ width: `${Math.max(4, sc.pct)}%` }} />
                        <em>{Math.round(sc.pct)}%</em>
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className="mk-tr-none">{facets.loading ? "세는 중…" : "—"}</span>
                )}
              </dd>
              <span>최근 {cnt(facets.sampled)}건 기준</span>
            </div>
          </dl>
        )}

        {keywords.length > 1 && !!cmpStats.length && (
          <div className="mk-tr-cmp">
            {cmpStats.map((c, i) => (
              <div key={c.query} className="mk-tr-cmp-row">
                <span className="mk-tr-cmp-name">
                  <i className={`mk-tr-dot c${i % 6}`} aria-hidden="true" />
                  {c.query}
                </span>
                {/* 라벨을 값 앞에 둔다 — '181건 하루' 는 뒤늦게 뜻이 붙는다(Bishop) */}
                <span className="mk-tr-cmp-v">
                  <em>하루</em>
                  <b>{perDay(c.perDay)}건</b>
                </span>
                <span className="mk-tr-cmp-v">
                  <em>최고</em>
                  <b>{whenLabel(c.peakAt, activeIv)}</b>
                </span>
              </div>
            ))}
          </div>
        )}

        {trend.error && <div className="mk-none mk-tr-msg">{trend.error}</div>}
        {trend.loading && !trend.data && <div className="mk-none mk-tr-msg">추이를 그리는 중…</div>}
        <div ref={chartRef} className="mk-tr-chart" />

        {/* 차트를 끌고 나면 시선이 여기 있다 — 같은 검색 버튼을 아래에도 둔다(Bishop) */}
        <div className="mk-tr-foot">
          {dirty ? (
            <span className="mk-tr-brushed">
              <b>{draft.from} ~ {draft.to}</b> 구간을 골랐습니다 — 누르면 이 기간으로 다시 조회합니다.
            </span>
          ) : (
            <span className="mk-tr-brushed">
              {zoomable
                ? "차트 아래 손잡이를 끌면 그 구간이 위 기간에 들어갑니다."
                : "기간을 바꾸고 검색하면 차트와 기사가 함께 바뀝니다."}
            </span>
          )}
          {searchBtn}
        </div>
      </section>

      {/* 기사 패널 폭 조절 */}
      <button
        type="button"
        className={`mk-grip${dragging ? " dragging" : ""}`}
        title="너비 조절"
        aria-label="기사 목록 너비 조절"
        onMouseDown={startResize}
      />

      {/* ── 오른쪽: 고른 구간의 기사 ───────────────────────── */}
      <aside className="mk-tr-side" style={{ width: sideW }}>
        <div className="mk-tr-side-h">
          <strong>
            {news.total != null
              ? `${news.total.toLocaleString("ko-KR")}건의 기사`
              : "기사"}
          </strong>
          {/* 여러 키워드를 견줄 때는 어느 키워드의 기사인지 골라야 한다 — 섞으면
              어느 쪽 기사인지 알 수 없다 */}
          {keywords.length > 1 && (
            <div className="mk-tr-pick">
              {keywords.map((k, i) => (
                <button
                  key={k}
                  type="button"
                  className={`mk-tr-pick-b${k === shownKw ? " on" : ""}`}
                  onClick={() => setNewsKw(k)}
                >
                  <i className={`mk-tr-dot c${i % 6}`} aria-hidden="true" />
                  {k}
                </button>
              ))}
            </div>
          )}
          <span className="mk-tr-range">
            {range.from} ~ {range.to}
          </span>
        </div>

        <div className="mk-tr-news">
          {news.error && <div className="mk-none">{news.error}</div>}
          {!news.error && !news.items.length && (
            <div className="mk-none">
              {news.loading ? "기사를 찾는 중…" : "이 구간에는 기사가 없습니다. 기간을 넓혀 보세요."}
            </div>
          )}
          {news.items.map((n, i) => (
            <a
              key={n.id || i}
              className="mk-news-item"
              href={n.url || undefined}
              target="_blank"
              rel="noreferrer"
            >
              {n.thumb && <img className="mk-news-thumb" src={n.thumb} alt="" loading="lazy" />}
              <div className="mk-news-main">
                <div className="mk-news-title">{n.title || "(제목 없음)"}</div>
                <div className="mk-news-meta">
                  {[n.publisher, String(n.at || "").slice(0, 10)].filter(Boolean).join(" · ")}
                </div>
              </div>
            </a>
          ))}
          {news.capped && (
            <div className="mk-none">
              더 뒤쪽 기사는 이 화면에서 보여줄 수 없습니다 — 구간을 좁혀 주세요.
            </div>
          )}
        </div>

        {!!news.items.length && (
          <div className="mk-pager">
            <button
              type="button"
              className="mk-pager-btn"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={news.loading || page <= 1}
            >
              이전
            </button>
            {/* 번호 페이저 — '2페이지' 한 칸만 있으면 몇 페이지가 있는지, 지금 어디인지
                알 수 없다(Bishop). 페이지 수는 서버가 상한(10)까지 알려주고,
                열 개를 다 늘어놓으면 좁은 패널을 다 먹어서 줄임표로 접는다. */}
            <div className="mk-pager-nums">
              {pageItems(page, news.pages).map((n, i) => (
                n === "…" ? (
                  <span key={`gap${i}`} className="mk-pager-gap" aria-hidden="true">…</span>
                ) : (
                  <button
                    key={n}
                    type="button"
                    className={`mk-pager-n${n === page ? " on" : ""}`}
                    onClick={() => setPage(n)}
                    disabled={news.loading}
                    aria-current={n === page ? "page" : undefined}
                  >
                    {n}
                  </button>
                )
              ))}
            </div>
            <button
              type="button"
              className="mk-pager-btn"
              onClick={() => setPage((p) => p + 1)}
              disabled={news.loading || !news.hasNext || page >= news.pages}
            >
              다음
            </button>
          </div>
        )}
      </aside>
      </div>
    </div>
  );
}
